import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Sparkles, Loader2, Plus, Trash2, Edit3, RefreshCw, Save, X, Upload, Image as ImageIcon, GripVertical, Power, PowerOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from '../../hooks/use-toast';

import { API } from '../../config';
const tok = () => localStorage.getItem('tp_admin_token');

const GRADIENTS = [
  { id: 'violet',   label: 'Violet', cls: 'bnr-violet' },
  { id: 'coral',    label: 'Coral',  cls: 'bnr-coral' },
  { id: 'lime',     label: 'Lime',   cls: 'bnr-lime' },
  { id: 'pink',     label: 'Pink',   cls: 'bnr-pink' },
  { id: 'sky',      label: 'Sky',    cls: 'bnr-sky' },
  { id: 'sunset',   label: 'Sunset', cls: 'bnr-sunset' },
  { id: 'midnight', label: 'Midnight', cls: 'bnr-midnight' },
];

const emptyBanner = () => ({
  title: '', subtitle: '', cta_text: 'Book Now', cta_link: '/',
  image_url: '', gradient: 'violet', layout: 'half', code: '', badge: '', active: true, order: 0,
});

const AdminBanners = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // banner being edited, or 'new'
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/banners`, { headers: { Authorization: `Bearer ${tok()}` } });
      setItems(r.data || []);
    } catch (e) {
      toast({ title: 'Failed to load banners' });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => setEditing(emptyBanner());

  const startEdit = (b) => setEditing({ ...b });

  const saveBanner = async () => {
    if (!editing) return;
    if (!editing.title?.trim()) { toast({ title: 'Title required' }); return; }
    setSaving(true);
    try {
      const body = { ...editing };
      let saved;
      if (editing.id) {
        saved = (await axios.put(`${API}/admin/banners/${editing.id}`, body, { headers: { Authorization: `Bearer ${tok()}` } })).data;
      } else {
        saved = (await axios.post(`${API}/admin/banners`, body, { headers: { Authorization: `Bearer ${tok()}` } })).data;
      }
      toast({ title: editing.id ? 'Banner updated' : 'Banner created', description: saved.title });
      setEditing(null);
      load();
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail });
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this banner?')) return;
    try {
      await axios.delete(`${API}/admin/banners/${id}`, { headers: { Authorization: `Bearer ${tok()}` } });
      toast({ title: 'Deleted' });
      load();
    } catch (e) {
      toast({ title: 'Delete failed' });
    }
  };

  const toggleActive = async (b) => {
    try {
      await axios.put(`${API}/admin/banners/${b.id}`, { ...b, active: !b.active }, { headers: { Authorization: `Bearer ${tok()}` } });
      load();
    } catch (e) {
      toast({ title: 'Update failed' });
    }
  };

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/admin/upload?folder=banners`, fd, {
        headers: { Authorization: `Bearer ${tok()}`, 'Content-Type': 'multipart/form-data' },
      });
      setEditing(e => ({ ...e, image_url: r.data.url }));
      toast({ title: 'Image uploaded' });
    } catch (e) {
      toast({ title: 'Upload failed', description: (e.response?.data?.detail || e.message)?.slice(0, 200) });
    } finally { setUploading(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-tp-ink flex items-center gap-2"><Sparkles className="text-tp-violet" size={20} /> Promo Banners</h1>
          <p className="text-sm text-gray-500">Manage the rotating banner slides shown at the top of the home page (web + app).</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={load} variant="outline" size="sm"><RefreshCw size={14} className="mr-1.5" /> Refresh</Button>
          <Button onClick={startNew} className="btn-violet rounded-full" data-testid="add-banner-btn"><Plus size={14} className="mr-1.5" /> Add Banner</Button>
        </div>
      </div>

      {/* List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {loading ? (
          <div className="col-span-2 text-center py-10 text-gray-400"><Loader2 className="animate-spin mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="col-span-2 bg-white rounded-3xl p-10 text-center border border-dashed border-gray-200">
            <ImageIcon size={36} className="mx-auto text-gray-300 mb-3" />
            <div className="text-gray-500 text-sm mb-3">No banners yet</div>
            <Button onClick={startNew} className="btn-violet rounded-full"><Plus size={14} className="mr-1.5" /> Create your first banner</Button>
          </div>
        ) : items.map(b => {
          const g = GRADIENTS.find(x => x.id === b.gradient) || GRADIENTS[0];
          return (
            <div key={b.id} data-testid={`banner-row-${b.id}`} className="bg-white rounded-3xl card-shadow border border-gray-100 overflow-hidden">
              <div className={`relative ${g.cls} bnr-blob p-4 min-h-[140px]`}>
                <div className="relative z-10">
                  {b.badge && <span className="pill-tag mb-2 text-tp-ink">{b.badge}</span>}
                  <div className="font-serif-display text-2xl text-white leading-tight">{b.title}</div>
                  <div className="text-xs text-white/80 mt-1 line-clamp-2">{b.subtitle}</div>
                </div>
                {!b.active && (
                  <span className="absolute top-2 right-2 bg-black/50 text-white text-[10px] font-bold px-2 py-0.5 rounded">INACTIVE</span>
                )}
              </div>
              <div className="p-3 flex items-center gap-2 text-xs text-gray-500">
                <span className="bg-gray-100 px-2 py-0.5 rounded font-mono">order {b.order}</span>
                <span className="bg-gray-100 px-2 py-0.5 rounded">→ {b.cta_link}</span>
                {b.code && <span className="bg-tp-violet-soft text-tp-violet-deep px-2 py-0.5 rounded font-bold">{b.code}</span>}
                <div className="ml-auto flex gap-1">
                  <button onClick={() => toggleActive(b)} title="Toggle active" className="p-1.5 hover:text-tp-violet">
                    {b.active ? <Power size={14} /> : <PowerOff size={14} className="text-red-500" />}
                  </button>
                  <button onClick={() => startEdit(b)} data-testid={`edit-banner-${b.id}`} className="p-1.5 hover:text-tp-violet"><Edit3 size={14} /></button>
                  <button onClick={() => del(b.id)} data-testid={`del-banner-${b.id}`} className="p-1.5 hover:text-red-600"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={o => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editing?.id ? <Edit3 size={16} /> : <Plus size={16} />} {editing?.id ? 'Edit Banner' : 'New Banner'}
            </DialogTitle>
          </DialogHeader>

          {editing && (
            <div className="space-y-4">
              {/* Live preview */}
              <div className="rounded-3xl overflow-hidden">
                {editing.image_url && (editing.layout || 'half') === 'full' ? (
                  <div data-testid="banner-preview-full" className="relative overflow-hidden min-h-[180px]">
                    <img src={editing.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(8,6,20,0.85) 0%, rgba(8,6,20,0.5) 42%, rgba(8,6,20,0.05) 78%)' }} />
                    <div className="relative z-10 p-6 min-h-[180px] flex flex-col justify-center w-3/5" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.45)' }}>
                      {editing.badge && <span className="pill-tag mb-2 text-white">{editing.badge}</span>}
                      <div className="font-serif-display text-3xl text-white">{editing.title || 'Banner title'}</div>
                      <div className="text-xs text-white/90 mt-1">{editing.subtitle || 'Subtitle here'}</div>
                      {editing.code && <div className="mt-2 inline-block bg-black/30 backdrop-blur text-white text-[11px] px-2 py-1 rounded-full w-fit">Code: {editing.code}</div>}
                    </div>
                  </div>
                ) : editing.image_url && (editing.layout || 'half') === 'half' ? (
                  <div data-testid="banner-preview-half" className={`relative overflow-hidden ${GRADIENTS.find(g => g.id === editing.gradient)?.cls || 'bnr-violet'} min-h-[180px]`}>
                    <img
                      src={editing.image_url}
                      alt=""
                      className="absolute right-0 top-0 h-full w-[62%] object-cover"
                      style={{ WebkitMaskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.6) 22%, #000 45%)', maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.6) 22%, #000 45%)' }}
                    />
                    <div className="relative z-10 p-6 min-h-[180px] flex flex-col justify-center w-[62%]" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.35)' }}>
                      {editing.badge && <span className="pill-tag mb-2 text-white">{editing.badge}</span>}
                      <div className="font-serif-display text-3xl text-white">{editing.title || 'Banner title'}</div>
                      <div className="text-xs text-white/90 mt-1">{editing.subtitle || 'Subtitle here'}</div>
                      {editing.code && <div className="mt-2 inline-block bg-black/30 backdrop-blur text-white text-[11px] px-2 py-1 rounded-full w-fit">Code: {editing.code}</div>}
                    </div>
                  </div>
                ) : (
                  <div data-testid="banner-preview-card" className={`relative bnr-blob ${GRADIENTS.find(g => g.id === editing.gradient)?.cls || 'bnr-violet'} p-6 min-h-[180px]`}>
                    <div className="relative z-10 grid grid-cols-12 gap-3 items-center">
                      <div className="col-span-8">
                        {editing.badge && <span className="pill-tag mb-2 text-tp-ink">{editing.badge}</span>}
                        <div className="font-serif-display text-3xl text-white">{editing.title || 'Banner title'}</div>
                        <div className="text-xs text-white/85 mt-1">{editing.subtitle || 'Subtitle here'}</div>
                        {editing.code && <div className="mt-2 inline-block bg-black/25 backdrop-blur text-white text-[11px] px-2 py-1 rounded-full">Code: {editing.code}</div>}
                      </div>
                      <div className="col-span-4 flex justify-end text-5xl opacity-80">🚆</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Layout picker */}
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">Banner Layout</label>
                <div className="grid grid-cols-2 gap-3 mt-1">
                  {[
                    { id: 'full', label: 'Full Banner', desc: 'Image fills the whole banner with an overlay (OTT style)' },
                    { id: 'half', label: 'Half Banner', desc: 'Image on the right half, gradient fades in from the left' },
                  ].map(opt => {
                    const on = (editing.layout || 'half') === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        data-testid={`banner-layout-${opt.id}`}
                        onClick={() => setEditing({ ...editing, layout: opt.id })}
                        className={`text-left rounded-2xl border-2 p-3 transition-all ${on ? 'border-tp-violet bg-tp-violet-soft/50' : 'border-gray-200 hover:border-tp-violet/40'}`}
                      >
                        <div className={`text-sm font-bold ${on ? 'text-tp-violet-deep' : 'text-tp-ink'}`}>{opt.label}</div>
                        <div className="text-[11px] text-gray-500 mt-0.5">{opt.desc}</div>
                      </button>
                    );
                  })}
                </div>
                <div className="text-[11px] text-gray-400 mt-1">Layouts apply only when a banner image is uploaded; without an image a gradient card is shown.</div>
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Title" value={editing.title} onChange={v => setEditing({ ...editing, title: v })} testid="banner-title" />
                <Field label="Badge (small pill)" value={editing.badge} onChange={v => setEditing({ ...editing, badge: v })} placeholder="NEW / LIMITED / LIVE" />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">Subtitle</label>
                <Textarea value={editing.subtitle || ''} rows={2} onChange={ev => setEditing({ ...editing, subtitle: ev.target.value })} />
              </div>
              <div className="grid md:grid-cols-3 gap-3">
                <Field label="CTA text" value={editing.cta_text} onChange={v => setEditing({ ...editing, cta_text: v })} />
                <Field label="CTA link" value={editing.cta_link} onChange={v => setEditing({ ...editing, cta_link: v })} placeholder="/live-train-status" />
                <Field label="Promo code" value={editing.code} onChange={v => setEditing({ ...editing, code: v })} placeholder="TPFOOD40" />
              </div>
              <div className="grid md:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase">Gradient</label>
                  <Select value={editing.gradient} onValueChange={v => setEditing({ ...editing, gradient: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {GRADIENTS.map(g => <SelectItem key={g.id} value={g.id}>{g.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Field label="Order" type="number" value={String(editing.order ?? 0)} onChange={v => setEditing({ ...editing, order: parseInt(v) || 0 })} />
                <div className="flex items-end gap-2">
                  <label className="text-xs font-bold text-gray-500 uppercase">Active</label>
                  <Switch checked={!!editing.active} onCheckedChange={v => setEditing({ ...editing, active: v })} />
                </div>
              </div>

              {/* Image upload */}
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">Banner image (right-side product/photo) — optional</label>
                <div className="mt-1 flex items-center gap-2">
                  <Input data-testid="banner-image-url" value={editing.image_url || ''} onChange={ev => setEditing({ ...editing, image_url: ev.target.value })} placeholder="https://cdn.../banner.png" />
                  <input ref={fileRef} type="file" accept="image/*" hidden onChange={ev => onUpload(ev.target.files?.[0])} />
                  <Button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-banner-img" className="btn-violet rounded-2xl shrink-0">
                    {uploading ? <Loader2 className="animate-spin mr-1.5" size={14} /> : <Upload size={14} className="mr-1.5" />} Upload
                  </Button>
                </div>
                <div className="text-[11px] text-gray-400 mt-1">Uploads go to your Bunny CDN (configure in Settings → Storage)</div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}><X size={14} className="mr-1.5" /> Cancel</Button>
            <Button onClick={saveBanner} disabled={saving} data-testid="save-banner-btn" className="btn-violet rounded-full">
              {saving ? <Loader2 className="animate-spin mr-1.5" size={14} /> : <Save size={14} className="mr-1.5" />} Save Banner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', placeholder, testid }) => (
  <div>
    <label className="text-xs font-bold text-gray-500 uppercase">{label}</label>
    <Input data-testid={testid} type={type} value={value || ''} onChange={ev => onChange(ev.target.value)} placeholder={placeholder} className="mt-1" />
  </div>
);

export default AdminBanners;
